import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getTour, type OnboardingAudience } from "@/lib/onboarding/tours";

export type OnboardingStatus = "not_started" | "in_progress" | "completed" | "dismissed";

export interface OnboardingState {
  status: OnboardingStatus;
  lastStepIndex: number;
  lastStepId: string | null;
  completedAt: string | null;
  /** Tour version the user last completed. */
  tourVersion: number;
}

const DEFAULT_STATE: OnboardingState = {
  status: "not_started",
  lastStepIndex: 0,
  lastStepId: null,
  completedAt: null,
  tourVersion: 0,
};

/** Lightweight first-party product event. Never blocks the UI. */
export async function logProductEvent(
  event: string,
  audience?: string,
  entitySlug?: string,
  meta: Record<string, unknown> = {}
) {
  try {
    const { data } = await supabase.auth.getUser();
    const userId = data.user?.id;
    if (!userId) return;
    await supabase.from("product_events").insert({
      user_id: userId,
      event,
      audience: audience ?? null,
      entity_slug: entitySlug ?? null,
      meta: meta as never,
    });
  } catch {
    /* analytics must never break the experience */
  }
}

/**
 * Per-user onboarding state for a given audience.
 * Onboarding never gates access — every consumer can close or skip freely.
 */
export function useOnboarding(audience: OnboardingAudience | null) {
  const [userId, setUserId] = useState<string | null>(null);
  const [state, setState] = useState<OnboardingState>(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!audience) {
      setLoading(false);
      return;
    }
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id ?? null;
    setUserId(uid);
    if (!uid) {
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from("user_onboarding_state")
      .select("status, last_step_index, last_step_id, completed_at, tour_version")
      .eq("user_id", uid)
      .eq("audience", audience)
      .maybeSingle();
    if (error) {
      // Surface nothing to the user, but do not silently pretend it is complete.
      console.error("Failed to load onboarding state", error);
    }
    setState(
      data
        ? {
            status: (data.status as OnboardingStatus) ?? "not_started",
            lastStepIndex: data.last_step_index ?? 0,
            lastStepId: data.last_step_id ?? null,
            completedAt: data.completed_at ?? null,
            tourVersion: (data as { tour_version?: number }).tour_version ?? 1,
          }
        : DEFAULT_STATE
    );
    setLoading(false);
  }, [audience]);

  useEffect(() => {
    void load();
  }, [load]);

  const persist = useCallback(
    async (patch: Partial<Record<string, unknown>>, next: Partial<OnboardingState>) => {
      if (!userId || !audience) return;
      setState((prev) => ({ ...prev, ...next }));
      const { error } = await supabase.from("user_onboarding_state").upsert(
        {
          user_id: userId,
          audience,
          ...patch,
        } as never,
        { onConflict: "user_id,audience" }
      );
      if (error) {
        console.error("Failed to save onboarding state", error);
        throw new Error(error.message);
      }
    },
    [userId, audience]
  );

  const start = useCallback(async () => {
    await persist(
      { status: "in_progress", started_at: new Date().toISOString(), last_step_index: 0 },
      { status: "in_progress", lastStepIndex: 0 }
    );
    void logProductEvent("onboarding_started", audience ?? undefined);
  }, [persist, audience]);

  const saveStep = useCallback(
    async (index: number) => {
      const tour = audience ? getTour(audience) : null;
      const stepId = tour?.steps[index]?.id ?? null;
      await persist(
        { status: "in_progress", last_step_index: index, last_step_id: stepId },
        { status: "in_progress", lastStepIndex: index, lastStepId: stepId }
      );
    },
    [persist, audience]
  );

  const complete = useCallback(async () => {
    const now = new Date().toISOString();
    const version = audience ? getTour(audience).version : 1;
    await persist(
      { status: "completed", completed_at: now, tour_version: version },
      { status: "completed", completedAt: now, tourVersion: version }
    );
    void logProductEvent("onboarding_completed", audience ?? undefined);
  }, [persist, audience]);

  const skip = useCallback(async () => {
    await persist(
      { status: "dismissed", dismissed_at: new Date().toISOString() },
      { status: "dismissed" }
    );
    void logProductEvent("onboarding_skipped", audience ?? undefined);
  }, [persist, audience]);

  const restart = useCallback(async () => {
    await persist(
      {
        status: "in_progress",
        last_step_index: 0,
        last_step_id: null,
        completed_at: null,
        dismissed_at: null,
        started_at: new Date().toISOString(),
      },
      { status: "in_progress", lastStepIndex: 0, lastStepId: null, completedAt: null }
    );
    void logProductEvent("onboarding_restarted", audience ?? undefined);
  }, [persist, audience]);

  /** True when the tour has been updated since the user last completed it. */
  const hasNewerVersion =
    !!audience && state.status === "completed" && getTour(audience).version > state.tourVersion;

  return { userId, state, loading, hasNewerVersion, start, saveStep, complete, skip, restart, reload: load };
}
